import {Base_url} from '@app/config';
import axios from 'axios';
import {useQuery, useMutation} from '@tanstack/react-query';
import {apiClient} from '@app/ui/http/query-client';
import {toast} from '@app/ui/toast/toast';
import {message} from '@app/i18n/message';

export async function fetchDigitalSignatureTemplateId(id: any) {
  const response = await axios.get(`${Base_url}get-template/${id}`);
  return (response == null ? void 0 : response.data)
    ? (response.data.data ?? response.data)
    : response.data;
}

export async function fetchAllTransferedDocs(walletAddress: any) {
  try {
    const response = await axios.get(
      `${Base_url}file-entries-by-wallet/${walletAddress}`,
    );
    const data = response?.data || [];
    return data;
  } catch (e) {
    return [];
  }
}

// Helper to get user identifier (own_user_id, wallet address, or email)
function getUserIdentifier() {
  // Try own_user_id first (most reliable identifier)
  const ownUserId = localStorage.getItem('own_user_id');
  if (ownUserId) return ownUserId;

  // Try wallet address second (for wallet auth)
  const address = (window as any).wagmiAddress; // Set by useAccount hook
  if (address) return address;

  // Fallback to email (for Gmail auth)
  const authType = localStorage.getItem('authType');
  const userEmail = localStorage.getItem('userEmail');
  if (authType === 'gmail' && userEmail) return userEmail;

  return null;
}

// Fetch transaction entries
export function useTransactionEntries(params: any = {}) {
  const identifier = getUserIdentifier();
  
  return useQuery({
    queryKey: ['transactions', params, identifier],
    queryFn: async () => {
      try {
        if (!identifier) {
          console.warn('No user identifier found for transactions');
          return [];
        }
        
        const response = await apiClient.get(`getTransactions/${encodeURIComponent(identifier)}`, {
          params: {
            ...params,
            type: 'transactions'
          }
        });
        console.log('Transactions Response:', response?.data);
        return response?.data?.data || [];
      } catch (error) {
        console.error('Error fetching transactions:', error);
        return [];
      }
    },
    enabled: !!identifier, // Only run query if we have an identifier
  });
}

// Fetch payment history entries
export function usePaymentHistoryEntries(params: any = {}) {
  const identifier = getUserIdentifier();
  
  return useQuery({
    queryKey: ['payment-history', params, identifier],
    queryFn: async () => {
      try {
        if (!identifier) {
          console.warn('No user identifier found for payment history');
          return [];
        }
        
        const response = await apiClient.get(`getPayHistory/${encodeURIComponent(identifier)}`, {
          params: {
            ...params,
            type: 'payment_history'
          }
        });
        console.log('Payment History Response:', response?.data);
        return response?.data?.data || [];
      } catch (error) {
        console.error('Error fetching payment history:', error);
        return [];
      }
    },
    enabled: !!identifier, // Only run query if we have an identifier
  });
}

// Fetch current balance
export function useCurrentBalance() {
  const identifier = getUserIdentifier();
  
  return useQuery({
    queryKey: ['currentBalance', identifier],
    queryFn: async () => {
      try {
        if (!identifier) {
          console.warn('No user identifier found for balance');
          return { balance: '0', formatted: '₹0.00' };
        }
        
        const response = await apiClient.get(`current-balance/${encodeURIComponent(identifier)}`);
        console.log('Balance Response:', response?.data);
        return response?.data || { balance: '0', formatted: '₹0.00' };
      } catch (error) {
        console.error('Error fetching current balance:', error);
        return { balance: '0', formatted: '₹0.00' };
      }
    },
    enabled: !!identifier, // Only run query if we have an identifier
    staleTime: 30000, // Cache for 30 seconds
    refetchInterval: 60000, // Refetch every 60 seconds
  });
}

// ============ STRIPE PAYMENT FUNCTIONS ============

interface ClientSecretPayload {
  amount: number;
  return_url: string;
}

// Fetch client secret for Stripe checkout
async function fetchClientSecret(payload: ClientSecretPayload) {
  // Get user identifier
  const identifier = getUserIdentifier();
  if (!identifier) {
    throw new Error('User not authenticated');
  }
  
  // Add identifier to payload
  const payloadWithIdentifier = {
    ...payload,
    identifier: identifier,
  };
  
  const { data } = await apiClient.post(`create-checkout-session`, payloadWithIdentifier);
  return data;
}

export function getClientSecret() {
  return useMutation({
    mutationFn: (payload: ClientSecretPayload) => fetchClientSecret(payload),
    onSuccess: () => {
      console.log('Client secret fetched successfully');
    },
    onError: (err: any) => {
      console.error('Error fetching client secret', err);
      const errorMessage = err?.response?.data?.error || 'Error creating payment session';
      toast.danger(errorMessage);
    },
  });
}

// Fetch payment status
export async function fetchStatus(sessionId: string) {
  const { data } = await apiClient.get(`payment-status?session_id=${sessionId}`);
  return data;
}
